import { useId, type ComponentProps, type ReactNode } from "react";

import { cn } from "../../lib/cn";
import { FieldContext, useFieldControl, type FieldControlProps } from "./field-context";

export interface FieldProps {
	readonly label: string;
	/** Validation message; when set, the control is marked invalid and described by it. */
	readonly error?: string;
	/** Span both columns of a two-column form grid. */
	readonly full?: boolean;
	readonly children: ReactNode;
}

/** A label above one control (Input, Select or Textarea) and an optional error message below it. */
export function Field({ label, error, full = false, children }: FieldProps) {
	const id = useId();
	const errorId = `${id}-error`;
	const control: FieldControlProps =
		error === undefined ? { id } : { id, "aria-describedby": errorId, "aria-invalid": true };

	return (
		<div className={cn("flex flex-col gap-1", full && "col-span-2")}>
			<label htmlFor={id} className="text-micro font-semibold tracking-caps text-ink-2 uppercase">
				{label}
			</label>
			<FieldContext value={control}>{children}</FieldContext>
			{error === undefined ? null : (
				<p id={errorId} className="text-tiny text-alert">
					{error}
				</p>
			)}
		</div>
	);
}

const controlClassName =
	"w-full box-border border border-line rounded-ctl bg-bg text-ctl text-ink font-sans outline-none transition-colors duration-[120ms] placeholder:text-ink-3 focus:border-line-2 focus:bg-surface aria-invalid:border-alert";

export function Input({ className, ...props }: ComponentProps<"input">) {
	const control = useFieldControl();
	return <input {...control} {...props} className={cn(controlClassName, "h-8 px-2.5", className)} />;
}

export function Select({ className, ...props }: ComponentProps<"select">) {
	const control = useFieldControl();
	return <select {...control} {...props} className={cn(controlClassName, "h-8 cursor-pointer px-2.5", className)} />;
}

export function Textarea({ className, ...props }: ComponentProps<"textarea">) {
	const control = useFieldControl();
	return (
		<textarea
			{...control}
			{...props}
			className={cn(controlClassName, "min-h-16 resize-y px-2.5 py-2 leading-[1.45]", className)}
		/>
	);
}

export interface ComboProps extends Omit<ComponentProps<"input">, "list"> {
	/** What the organization has used before; the browser offers these, and anything else may still be typed. */
	readonly options: readonly string[];
}

/** Text input with a dropdown of values already in use. Nothing is validated against them: a new value is the point. */
export function Combo({ options, ...props }: ComboProps) {
	const listId = useId();
	return (
		<>
			<Input {...props} list={listId} />
			<datalist id={listId}>
				{options.map(option => (
					<option key={option} value={option} />
				))}
			</datalist>
		</>
	);
}
